import { useState, useEffect } from 'react';
import { Card, Badge, Toggle, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { MODULE_DEFINITIONS } from '@mirai/shared';
import { useRole } from '@/hooks/useRole';
import styles from './Master.module.css';

interface PortalMaster {
  id: string;
  name: string;
  endpoint: string | null;
  status: string;
  last_sync_at: string | null;
}

interface DocTemplate {
  id: string;
  name: string;
  version: string;
  updated_at: string;
}

const statusLabel: Record<string, string> = { active: '連携済', configuring: '設定中', inactive: '無効' };

const moduleList = MODULE_DEFINITIONS;

export function Master() {
  const { isSuperAdmin, isAdmin } = useRole();
  const { data: portals, loading: pLoading } = useApi<PortalMaster[]>('/console/master/portals');
  const { data: templates, loading: tLoading } = useApi<DocTemplate[]>('/console/master/templates');

  const defaultModuleState = Object.fromEntries(moduleList.map(m => [m.id, m.id !== 'ai_agent']));
  const [moduleStates, setModuleStates] = useState<Record<string, boolean>>(defaultModuleState);
  const [saving, setSaving] = useState(false);

  // サイト設定
  const { data: siteSettings, refetch: refetchSettings } = useApi<Record<string, string>>('/console/settings');
  const [contactForm, setContactForm] = useState({ support_email: '', support_phone: '', support_hours: '' });
  const [savingContact, setSavingContact] = useState(false);
  const [trialDays, setTrialDays] = useState('14');
  const [savingTrial, setSavingTrial] = useState(false);
  const [invoiceRegNo, setInvoiceRegNo] = useState('');
  const [invoiceZip, setInvoiceZip] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [savingInvoice, setSavingInvoice] = useState(false);

  useEffect(() => {
    if (siteSettings) {
      setContactForm({
        support_email: siteSettings.support_email ?? '',
        support_phone: siteSettings.support_phone ?? '',
        support_hours: siteSettings.support_hours ?? '',
      });
      setTrialDays(siteSettings.trial_days ?? '14');
      setInvoiceRegNo(siteSettings.invoice_registration_no ?? '');
      setInvoiceZip(siteSettings.invoice_issuer_zip ?? '');
      setInvoiceAddress(siteSettings.invoice_issuer_address ?? '');
    }
  }, [siteSettings]);

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      await api('/console/settings', { method: 'PUT', body: JSON.stringify(contactForm) });
      toast('連絡先を保存しました');
      refetchSettings();
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSavingContact(false); }
  };

  const handleSaveTrial = async () => {
    const days = Number(trialDays);
    if (!days || days < 1 || days > 365) { toast('1〜365の範囲で入力してください', 'error'); return; }
    setSavingTrial(true);
    try {
      await api('/console/settings', { method: 'PUT', body: JSON.stringify({ trial_days: String(days) }) });
      toast('トライアル日数を保存しました');
      refetchSettings();
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSavingTrial(false); }
  };

  const handleSaveInvoice = async () => {
    setSavingInvoice(true);
    try {
      await api('/console/settings', { method: 'PUT', body: JSON.stringify({ invoice_registration_no: invoiceRegNo.trim(), invoice_issuer_zip: invoiceZip.trim(), invoice_issuer_address: invoiceAddress.trim() }) });
      toast('インボイス設定を保存しました');
      refetchSettings();
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSavingInvoice(false); }
  };

  const handleToggle = (id: string) => {
    setModuleStates(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settings: Record<string, string> = {};
      for (const [id, enabled] of Object.entries(moduleStates)) {
        settings[`module.${id}.enabled`] = String(enabled);
      }
      await api('/console/master/config', { method: 'POST', body: JSON.stringify({ settings }) });
      toast('設定を保存しました', 'success');
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSaving(false); }
  };

  if (pLoading || tLoading) return <div className={styles.page}>読み込み中...</div>;

  return (
    <div className={styles.page}>
      {/* サポート連絡先 */}
      <Card title="📞 サポート連絡先" actions={
        isAdmin ? <button className={styles.btnSecondary} onClick={handleSaveContact} disabled={savingContact}>{savingContact ? '保存中...' : '保存する'}</button> : undefined
      }>
        <div className={styles.contactGrid}>
          <div className={styles.contactField}>
            <label className={styles.contactLabel}>メールアドレス</label>
            <input className={styles.contactInput} type="email" value={contactForm.support_email} onChange={(e) => setContactForm(f => ({ ...f, support_email: e.target.value }))} disabled={!isAdmin} />
          </div>
          <div className={styles.contactField}>
            <label className={styles.contactLabel}>電話番号</label>
            <input className={styles.contactInput} type="tel" value={contactForm.support_phone} onChange={(e) => setContactForm(f => ({ ...f, support_phone: e.target.value }))} disabled={!isAdmin} />
          </div>
          <div className={styles.contactField}>
            <label className={styles.contactLabel}>営業時間</label>
            <input className={styles.contactInput} type="text" value={contactForm.support_hours} onChange={(e) => setContactForm(f => ({ ...f, support_hours: e.target.value }))} disabled={!isAdmin} />
          </div>
        </div>
      </Card>

      {/* トライアル設定 */}
      <Card title="⏱ トライアル設定" actions={
        isAdmin ? <button className={styles.btnSecondary} onClick={handleSaveTrial} disabled={savingTrial}>{savingTrial ? '保存中...' : '保存する'}</button> : undefined
      }>
        <div className={styles.contactGrid}>
          <div className={styles.contactField}>
            <label className={styles.contactLabel}>トライアル期間（日数）</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                className={styles.contactInput}
                type="number"
                min={1}
                max={365}
                value={trialDays}
                onChange={e => setTrialDays(e.target.value)}
                disabled={!isAdmin}
                style={{ width: 100 }}
              />
              <span style={{ fontSize: 13, color: 'var(--ink2)' }}>日間</span>
            </div>
            <p style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
              新規トライアル承認時に適用されます。既存テナントには影響しません。
            </p>
          </div>
        </div>
      </Card>

      {/* インボイス設定 */}
      <Card title="🧾 インボイス設定" actions={
        isAdmin ? <button className={styles.btnSecondary} onClick={handleSaveInvoice} disabled={savingInvoice}>{savingInvoice ? '保存中...' : '保存する'}</button> : undefined
      }>
        <div className={styles.contactGrid}>
          <div className={styles.contactField}>
            <label className={styles.contactLabel}>適格請求書発行事業者 登録番号</label>
            <input
              className={styles.contactInput}
              type="text"
              value={invoiceRegNo}
              onChange={(e) => setInvoiceRegNo(e.target.value)}
              placeholder="T1234567890123"
              disabled={!isAdmin}
            />
            <p style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
              インボイス制度対応。T + 13桁の数字。請求書 PDF に印字されます。
            </p>
          </div>
          <div className={styles.contactField}>
            <label className={styles.contactLabel}>発行者 郵便番号（請求書に印字）</label>
            <input
              className={styles.contactInput}
              type="text"
              value={invoiceZip}
              onChange={(e) => setInvoiceZip(e.target.value)}
              placeholder="〒000-0000"
              disabled={!isAdmin}
              style={{ maxWidth: 160 }}
            />
          </div>
          <div className={styles.contactField}>
            <label className={styles.contactLabel}>発行者 住所（請求書に印字）</label>
            <input
              className={styles.contactInput}
              type="text"
              value={invoiceAddress}
              onChange={(e) => setInvoiceAddress(e.target.value)}
              placeholder="東京都○○区○○1-2-3"
              disabled={!isAdmin}
            />
          </div>
        </div>
      </Card>

      {/* ドメイン設計 + 認証設定 */}
      <div className={styles.grid2}>
        <Card title="🌐 ドメイン設計">
          <div className={styles.infoGrid}>
            {[
              ['管理コンソール URL', 'estate.jiayin.jp'],
              ['テナント URL 形式', '{slug}.jiayin.jp'],
              ['カスタムドメイン', '{任意}.{独自ドメイン}'],
              ['REST API', 'estate.jiayin.jp/api'],
            ].map(([k, v]) => (
              <div key={k} className={styles.infoRow}>
                <span className={styles.infoKey}>{k}</span>
                <code className={styles.code}>{v}</code>
              </div>
            ))}
          </div>
        </Card>

        <Card title="🔐 認証設定">
          <div className={styles.infoGrid}>
            {[
              ['SuperAdmin JWT', 'RS256 / 8h TTL'],
              ['Tenant JWT', 'RS256 / 24h TTL'],
            ].map(([k, v]) => (
              <div key={k} className={styles.infoRow}>
                <span className={styles.infoKey}>{k}</span>
                <code className={styles.code}>{v}</code>
              </div>
            ))}
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>トークン共有</span>
              <Badge variant="red">完全非共有</Badge>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoKey}>DB 分離方式</span>
              <Badge variant="green">スキーマ分離</Badge>
            </div>
          </div>
        </Card>
      </div>

      {/* モジュール制御 */}
      <Card title="🔌 全テナント共通モジュール制御" actions={
        isSuperAdmin ? <button className={styles.btnSecondary} onClick={handleSave} disabled={saving}>{saving ? '保存中...' : '保存する'}</button> : undefined
      }>
        <div className={styles.moduleList}>
          {moduleList.map((m) => (
            <div key={m.id} className={styles.moduleRow}>
              <span className={styles.moduleIcon}>{m.icon}</span>
              <div className={styles.moduleInfo}>
                <div className={styles.moduleName}>{m.name}</div>
                <div className={styles.moduleDesc}>{m.desc}</div>
              </div>
              <Toggle checked={moduleStates[m.id] ?? true} disabled={m.required} onChange={() => handleToggle(m.id)} />
            </div>
          ))}
        </div>
      </Card>

      {/* ポータル連携先マスタ */}
      <Card title="ポータル連携先マスタ">
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>ポータル</th>
              <th className={styles.th}>ステータス</th>
              <th className={styles.th}>エンドポイント</th>
              <th className={styles.th}>最終同期</th>
            </tr>
          </thead>
          <tbody>
            {(portals ?? []).map((p) => (
              <tr key={p.id}>
                <td className={styles.td} style={{ fontWeight: 600 }}>{p.name}</td>
                <td className={styles.td}>
                  <span className={`${styles.statusDot} ${p.status === 'active' ? styles.active : styles.pending}`} />
                  {statusLabel[p.status] ?? p.status}
                </td>
                <td className={styles.td}><code className={styles.code}>{p.endpoint ?? '—'}</code></td>
                <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink3)' }}>
                  {p.last_sync_at ? new Date(p.last_sync_at).toLocaleString('ja-JP') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* 書類テンプレート */}
      <Card title="書類テンプレートマスタ">
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>テンプレート名</th>
              <th className={styles.th}>バージョン</th>
              <th className={styles.th}>最終更新</th>
            </tr>
          </thead>
          <tbody>
            {(templates ?? []).map((d) => (
              <tr key={d.id}>
                <td className={styles.td} style={{ fontWeight: 500 }}>{d.name}</td>
                <td className={styles.td}><code className={styles.code}>{d.version}</code></td>
                <td className={styles.td} style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink3)' }}>
                  {new Date(d.updated_at).toLocaleDateString('ja-JP')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
